import { defineNuxtPlugin } from '#app';
import { fetchOneEntry } from '@builder.io/sdk-vue';
import { useBuilderComponents } from '~/modules/builder-components/runtime/composables/builder-components';
// resolveComponents,
// loadAllComponents,
// getComponentByFriendlyName,

const resolveComponents = async (friendlyNames: string[], builderComponents: any) => {
  return {};
}

const getComponentByFriendlyName = async (friendlyName: string) => {}

type Input = {
  name: string;
  type: string;
  defaultValue?: any;
  [key: string]: any; // Allow additional properties like min, max, step
};

function deepSanitize(value: any): any {
  if (value === null || value === undefined) return value;
  const t = typeof value;
  if (t === 'function' || t === 'symbol') return undefined;
  if (t === 'string' || t === 'number' || t === 'boolean') return value;

  // Handle special objects
  if (value instanceof Date) return value.toISOString();
  // @ts-ignore URL may not exist in some runtimes
  if (typeof URL !== 'undefined' && value instanceof URL) return value.toString();
  if (value instanceof RegExp) return value.toString();
  // File/Blob or DOM nodes cannot be serialized
  if (typeof (value as any).arrayBuffer === 'function' || (value as any).nodeType) return undefined;

  if (Array.isArray(value)) {
    const arr = value.map((v) => deepSanitize(v)).filter((v) => v !== undefined);
    return arr;
  }
  if (t === 'object') {
    const plain: Record<string, any> = {};
    for (const [k, v] of Object.entries(value)) {
      const sanitized = deepSanitize(v);
      if (sanitized !== undefined) plain[k] = sanitized;
    }
    return plain;
  }
  return undefined;
}

function logComponentsDebug(components: any[], context: string) {
  try {
    const summary = components.map((c: any) => ({
      name: c?.name,
      inputs: Array.isArray(c?.inputs)
        ? c.inputs.map((i: any) => {
            const base: any = { name: i?.name, type: i?.type };
            if (i?.type === 'list') {
              const len = Array.isArray(i?.defaultValue) ? i.defaultValue.length : 0;
              const sample = len > 0 ? deepSanitize(i.defaultValue[0]) : undefined;
              base.defaultLen = len;
              base.sampleItem = sample;
              base.subFields = i?.subFields;
            } else if (i?.defaultValue !== undefined) {
              base.defaultValue = typeof i.defaultValue === 'object' ? deepSanitize(i.defaultValue) : i.defaultValue;
            }
            return base;
          })
        : []
    }));
    console.log('[builder-components][debug]', context, JSON.stringify(summary));
  } catch (e) {
    console.log('[builder-components][debug] failed to log components', e);
  }
}

function extractComponentNames(obj: any): string[] {
  const names: string[] = [];
  if (Array.isArray(obj)) {
    for (const item of obj) names.push(...extractComponentNames(item));
  } else if (obj && typeof obj === 'object') {
    // Extract friendly names from Builder.io component structure
    if (obj.component?.name) names.push(obj.component.name);
    for (const value of Object.values(obj)) {
      names.push(...extractComponentNames(value));
    }
  }
  return names;
}

function inferType(type: any): string {
  if (type === Boolean) return 'boolean';
  if (type === Number) return 'number';
  if (type === Object) return 'object';
  if (type === Array) return 'list';
  return 'text';
}

function extractInputsFromProps(props: any): Input[] {
  if (!props) return [];
  if (Array.isArray(props)) {
    return props.map((name: string) => ({ name, type: 'text' }));
  }
  const isLikelyColor = (name: string, def: any) => {
    const n = (name || '').toLowerCase();
    if (n.includes('color') || n.includes('colour')) return true;
    if (typeof def === 'string' && (def.startsWith('#') || def.startsWith('rgba(') || def.startsWith('hsl('))) return true;
    return false;
  };
  const isLikelyFile = (_name: string, def: any) => {
    // Only default value patterns should trigger 'file' to avoid false positives (e.g., alt text)
    if (typeof def === 'string' && /(\.(png|jpe?g|webp|svg|gif|mp4|webm|pdf)$)/i.test(def)) return true;
    if (typeof def === 'string' && /^https?:\/\//i.test(def)) return true;
    return false;
  };
  const isLikelyRichText = (name: string) => {
    const n = (name || '').toLowerCase();
    return n.includes('richtext') || n.includes('html') || n.includes('content') || n.endsWith('texttopbold') || n.endsWith('texttopsmall');
  };

  const inputs: Input[] = [];
  for (const [name, options] of Object.entries(props)) {
    const opt: any = typeof options === 'object' && options !== null ? options : { type: options };

    // Normalize default value safely
    let defaultValue = opt.default as any;
    if (typeof defaultValue === 'function') {
      try {
        defaultValue = defaultValue.length === 0 ? defaultValue() : undefined;
      } catch {
        defaultValue = undefined;
      }
    }

    // Base type from prop type first
    let type = inferType(opt.type);
    if (type !== 'list') {
      // Only apply heuristics for non-list types
      if (isLikelyColor(name, defaultValue)) type = 'color';
      else if (isLikelyFile(name, defaultValue)) type = 'file';
      else if (isLikelyRichText(name)) type = 'richText';
    }

    // Auto-generate subFields for list when defaultValue reveals structure
    let subFields: any[] | undefined = undefined;
    if (type === 'list' && Array.isArray(defaultValue) && defaultValue.length > 0) {
      const sample = defaultValue[0];
      if (typeof sample === 'string') {
        const elemType = isLikelyFile(name, sample) ? 'file' : 'text';
        subFields = [{ name: 'value', type: elemType }];
      } else if (sample && typeof sample === 'object' && !Array.isArray(sample)) {
        subFields = Object.entries(sample).map(([k, v]) => {
          let t = 'text';
          if (typeof v === 'number') t = 'number';
          else if (typeof v === 'boolean') t = 'boolean';
          else if (typeof v === 'string') t = isLikelyFile(k, v) ? 'file' : (isLikelyColor(k, v) ? 'color' : (isLikelyRichText(k) ? 'richText' : 'text'));
          return { name: k, type: t };
        });
      }
    }

    defaultValue = deepSanitize(defaultValue);

    const input: any = { name, type, defaultValue };
    if (subFields) input.subFields = subFields;
    inputs.push(input);
  }
  return inputs;
}

// No schema coercion: keep nested list defaults, only remove non-serializable values

function sanitizeInputs(inputs: any[]): any[] {
  if (!Array.isArray(inputs)) return [];
  return inputs.map((i: any) => {
    const base: any = { ...i };
    if (base.type === 'list') {
      base.defaultValue = Array.isArray(base.defaultValue)
        ? deepSanitize(base.defaultValue)
        : [];
      return base;
    }
    base.defaultValue = deepSanitize(base?.defaultValue);
    return base;
  });
}

export default defineNuxtPlugin(() => {
  return {
    provide: {
      async fetchBuilderPage(model: string, apiKey: string, urlPath: string, isEditing: boolean, builderComponents: any) {
        const content = await fetchOneEntry({
          model,
          apiKey,
          userAttributes: { urlPath },
        });
        
        // Handle case where content is null/undefined or has no data
        if (!content || !content.data) {
          return { 
            content: null, 
            components: [] 
          };
        }

        // Handle case where content.data exists but has no blocks
        if (!content.data.blocks || !Array.isArray(content.data.blocks) || content.data.blocks.length === 0) {
          return { 
            content: {
              ...content,
              data: {
                ...content.data,
                blocks: []
              }
            }, 
            components: [] 
          };
        }

        // Extract friendly names from the content
        const friendlyNames = extractComponentNames(content.data);
        if (isEditing) {
          console.log('[builder-components][debug] friendlyNames', JSON.stringify(friendlyNames));
        }

        // Prefer direct pass-through of pre-resolved components (preserves rich inputs & subFields)
        if (Array.isArray(builderComponents) && builderComponents.length > 0) {
          const passThrough = builderComponents
            .map((c: any) => ({
              name: c.friendlyName || c.name,
              canHaveChildren: c.canHaveChildren ?? true,
              inputs: sanitizeInputs(Array.isArray(c.inputs) ? c.inputs : []),
              description: c.description,
              keywords: c.keywords,
              category: c.category,
            }));
          if (isEditing) {
            logComponentsDebug(passThrough, 'pass-through after sanitize');
          }
          return { content, components: passThrough };
        }

        // Fallback: resolve by names (legacy/inference path)
        const registry = isEditing
          ? builderComponents
          : await resolveComponents(friendlyNames, builderComponents);

        let components: any[] = [];
        try {
          const entries: Array<[string, any]> = Array.isArray(registry)
            ? (registry as any[]).map((item: any) => [
                item?.friendlyName || item?.name || 'unknown',
                item,
              ])
            : Object.entries(registry || {});

          components = await Promise.all(
            entries.map(async ([friendlyName, value]) => {
              try {
                let loaded: any = null;
                if (typeof value === 'function') {
                  loaded = await value();
                } else if (value && typeof value === 'object') {
                  loaded = value.component ?? value;
                } else {
                  loaded = value;
                }

                const vueComponent = loaded?.default ?? loaded;
                const metadata = {
                  canHaveChildren: value?.canHaveChildren ?? true,
                  description: value?.description,
                  keywords: value?.keywords,
                  category: value?.category,
                  inputs: value?.inputs,
                } as any;

                const inputs = sanitizeInputs(Array.isArray(metadata.inputs)
                  ? metadata.inputs
                  : extractInputsFromProps(vueComponent?.props));

                return {
                  name: friendlyName,
                  canHaveChildren: metadata.canHaveChildren ?? true,
                  inputs,
                  description: metadata.description,
                  keywords: metadata.keywords,
                  category: metadata.category,
                };
              } catch (error) {
                console.warn(`Error processing component ${friendlyName}:`, error);
                return null;
              }
            })
          );

          components = components.filter(Boolean);
          if (isEditing) {
            logComponentsDebug(components, 'fallback after sanitize');
          }
        } catch (error) {
          console.error('Error loading components:', error);
        }

        // Only return serializable data - registry will be recreated on client side
        return { content, components };
      },
    },
  };
});
